import { useEffect, useMemo, useState } from "react";
import type {
  ComboBoxEventValue,
  InterstateOnChangeEvent
} from "@dealer-center/core-components";
import {
  Box,
  Button,
  Card,
  CardContent,
  ComboBox,
  CopyToClipboardIcon,
  Typography
} from "@dealer-center/core-components";
import type {
  NrtTestResult,
  NrtTestResultItem
} from "@dealer-center/ddc-pages-nrt-database-filter-tool-interface";
import { SR_ONLY_SX } from "./srOnlySx";
import { StatusChip } from "./StatusChip";

export interface TestResultPanelProps {
  result: NrtTestResult;
}

const HEADING_ID = "nrt-test-result-heading";

// Matches Rally's reference screenshot for the empty state verbatim.
const IDLE_MESSAGE = "Test results would go here";

// Both halves live in the label so the ComboBox's default label-substring
// search finds an item by either vin or inventoryRecordId (when present).
const toItemOption = (item: NrtTestResultItem) => {
  const inventoryRecordId =
    typeof item.inventoryRecordId === "string" ? item.inventoryRecordId : null;
  return {
    label: inventoryRecordId ? `${item.vin} || ${inventoryRecordId}` : item.vin,
    value: item.vin
  };
};

// Display-only: the Test action itself lives in RecordModal's shared action
// row (FR-001/FR-002), not inside this panel, so there is no button here.
export const TestResultPanel = ({ result }: TestResultPanelProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [copyAnnouncement, setCopyAnnouncement] = useState("");

  // FR-016: a new result always starts back at the first item. Keyed on `result`
  // itself (not `result.timestamp`, whose 1-second resolution can collide).
  useEffect(() => {
    setSelectedIndex(0);
    setCopyAnnouncement("");
  }, [result]);

  const hasItems = result.status === "COMPLETE" && result.items.length > 0;
  const selectedItem = hasItems
    ? (result.items[selectedIndex] ?? result.items[0])
    : null;
  const canCopy = result.status === "COMPLETE" || result.status === "ERROR";
  const selectedItemComboBoxValue = useMemo(
    () => (selectedItem ? [toItemOption(selectedItem)] : []),
    [selectedItem]
  );
  const itemComboBoxOptions = useMemo(
    () => result.items.map(toItemOption),
    [result.items]
  );

  const handleSelectItem = (
    event: InterstateOnChangeEvent<ComboBoxEventValue>
  ) => {
    const vin = event?.target.value?.[0]?.value;
    if (!vin) return;
    const index = result.items.findIndex(item => item.vin === vin);
    if (index >= 0) setSelectedIndex(index);
  };

  const handleCopy = async () => {
    const content = selectedItem
      ? JSON.stringify(selectedItem, null, 2)
      : JSON.stringify(
          {
            status: result.status,
            message: result.message,
            timestamp: result.timestamp
          },
          null,
          2
        );
    try {
      await navigator.clipboard.writeText(content);
      setCopyAnnouncement("Test result copied to clipboard.");
    } catch {
      setCopyAnnouncement("Could not copy the test result to the clipboard.");
    }
  };

  const heading = hasItems
    ? `Test Results (${result.items.length} ${
        result.items.length === 1 ? "item" : "items"
      })`
    : "Test Results";
  const isIdle = result.status === "IDLE";

  return (
    // role/aria-labelledby give the panel an accessible name so it is reachable
    // by rotor navigation instead of being an anonymous group of text.
    <Box aria-labelledby={HEADING_ID} role="group">
      <Card
        content={
          <CardContent>
            <Box
              sx={{
                alignItems: "center",
                display: "flex",
                gap: 3,
                justifyContent: "space-between",
                marginBottom: 2
              }}
            >
              <Typography id={HEADING_ID} variant="h5">
                {heading}
              </Typography>
              {hasItems && (
                <ComboBox
                  displayLabel
                  id="nrt-test-result-item-select"
                  label="Select VIN"
                  multiple={false}
                  options={itemComboBoxOptions}
                  sx={{ minWidth: "30rem" }}
                  value={selectedItemComboBoxValue}
                  onChange={handleSelectItem}
                />
              )}
            </Box>

            {isIdle && (
              <Box
                sx={{ borderBottom: "1px solid #e0e0e0", marginBottom: 2 }}
              />
            )}

            {!isIdle && (
              <Box
                sx={{
                  alignItems: "center",
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 1
                }}
              >
                <Box>
                  {canCopy && (
                    <Button
                      analyticsConfiguration={{
                        eventName: "nrt_mapping_tool_copy_test_result_json"
                      }}
                      buttonStyle="tertiary"
                      startIcon={
                        <CopyToClipboardIcon title="Copy to clipboard" />
                      }
                      onClick={handleCopy}
                    >
                      Copy JSON
                    </Button>
                  )}
                </Box>
                <StatusChip status={result.status} />
              </Box>
            )}

            <Box aria-live="polite" role="status" sx={SR_ONLY_SX}>
              {copyAnnouncement}
            </Box>

            {selectedItem ? (
              <Box
                component="pre"
                sx={{
                  backgroundColor: "#f5f5f5",
                  borderRadius: "4px",
                  fontSize: "0.875rem",
                  maxHeight: "24rem",
                  overflow: "auto",
                  padding: "1rem"
                }}
              >
                {JSON.stringify(selectedItem, null, 2)}
              </Box>
            ) : isIdle ? (
              <Box
                sx={{
                  alignItems: "center",
                  display: "flex",
                  justifyContent: "center",
                  padding: "3rem 0"
                }}
              >
                <Typography
                  sx={{ color: "#757575", fontStyle: "italic" }}
                  variant="body-md"
                >
                  {IDLE_MESSAGE}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body-md">{result.message}</Typography>
            )}
          </CardContent>
        }
      />
    </Box>
  );
};
